import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2 } from "lucide-react";
import { api, formatXAF, formatDate } from "@/lib/api";
import { useEntity } from "@/lib/entity";
import Modal, { ConfirmDialog } from "@/components/Modal";

const JOURNALS = [
  { code: "VTE", label: "Ventes" },
  { code: "ACH", label: "Achats" },
  { code: "BQ", label: "Banque" },
  { code: "CAI", label: "Caisse" },
  { code: "OD", label: "Opérations Diverses" },
];

export default function Accounting() {
  const { currentEntityId, entities } = useEntity();
  const [data, setData] = useState({ entries: [], total_debit: 0, total_credit: 0, balance: 0 });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [form, setForm] = useState(initForm(currentEntityId));

  useEffect(() => { load(); }, [currentEntityId]);
  useEffect(() => { setForm(initForm(currentEntityId)); }, [currentEntityId]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/accounting/${currentEntityId}`);
      setData(data);
    } finally { setLoading(false); }
  }

  async function submit(e) {
    e.preventDefault();
    const payload = {
      ...form,
      debit: parseFloat(form.debit || 0),
      credit: parseFloat(form.credit || 0),
    };
    await api.post("/accounting", payload);
    setShowForm(false);
    setForm(initForm(currentEntityId));
    load();
  }

  async function remove(id) {
    await api.delete(`/accounting/${id}`);
    load();
  }

  const opEntities = entities.filter((e) => e.id !== "GROUPE");

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="space-y-6" data-testid="accounting-page">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Total Débit" value={data.total_debit} />
        <Stat label="Total Crédit" value={data.total_credit} />
        <Stat label="Solde" value={data.balance} highlight />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Écritures comptables</h2>
          <p className="text-xs text-obsidian-400 mt-1">Plan OHADA · {data.entries.length} écriture(s)</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="accounting-new-button">
          <Plus size={14} /> Nouvelle écriture
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-obsidian-400 border-b border-line">
              <Th>Date</Th><Th>Entité</Th><Th>Journal</Th><Th>Compte</Th><Th>Tiers</Th><Th>Libellé</Th>
              <Th align="right">Débit</Th><Th align="right">Crédit</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={9} className="p-8 text-center text-obsidian-500">Chargement…</td></tr>)}
            {!loading && data.entries.length === 0 && (
              <tr><td colSpan={9} className="p-12 text-center text-obsidian-500">
                Aucune écriture pour l'instant.<br/>
                <button onClick={() => setShowForm(true)} className="mt-3 text-citron hover:underline text-sm">+ Ajouter la première</button>
              </td></tr>
            )}
            {data.entries.map((r) => (
              <tr key={r.id} className="border-b border-line/50 hover:bg-obsidian-850 transition-colors">
                <Td><span className="font-mono text-xs">{formatDate(r.date)}</span></Td>
                <Td><span className="pill bg-obsidian-800">{r.entity_id}</span></Td>
                <Td><span className="pill bg-citron/10 text-citron font-mono">{r.journal}</span></Td>
                <Td className="font-mono text-xs">{r.account_code}</Td>
                <Td className="text-obsidian-300">{r.third_party || "—"}</Td>
                <Td className="text-obsidian-200">{r.label}</Td>
                <Td align="right" className="font-mono tabular text-bad">{r.debit > 0 ? formatXAF(r.debit) : "—"}</Td>
                <Td align="right" className="font-mono tabular text-ok">{r.credit > 0 ? formatXAF(r.credit) : "—"}</Td>
                <Td align="right">
                  <button onClick={() => setConfirmId(r.id)} className="btn-ghost h-7 w-7 p-0 text-obsidian-500 hover:text-bad">
                    <Trash2 size={12} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={showForm} onClose={() => setShowForm(false)} title="Nouvelle écriture comptable" subtitle="Plan comptable OHADA">
        <form onSubmit={submit} className="space-y-4" data-testid="accounting-form">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label block">Entité</label>
              <select className="input" value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} required>
                {opEntities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label block">Date</label>
              <input type="date" className="input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
            </div>
            <div>
              <label className="label block">Journal</label>
              <select className="input" value={form.journal} onChange={(e) => setForm({ ...form, journal: e.target.value })}>
                {JOURNALS.map(j => <option key={j.code} value={j.code}>{j.code} — {j.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label block">Compte (code OHADA)</label>
              <input className="input font-mono" placeholder="411, 701, 601…" value={form.account_code} onChange={(e) => setForm({ ...form, account_code: e.target.value })} required />
            </div>
            <div>
              <label className="label block">Tiers</label>
              <input className="input" placeholder="Client / Fournisseur" value={form.third_party} onChange={(e) => setForm({ ...form, third_party: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="label block">Libellé</label>
            <input className="input" placeholder="Description de l'écriture" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label block">Débit (XAF)</label>
              <input type="number" min="0" step="1" className="input font-mono" value={form.debit} onChange={(e) => setForm({ ...form, debit: e.target.value })} />
            </div>
            <div>
              <label className="label block">Crédit (XAF)</label>
              <input type="number" min="0" step="1" className="input font-mono" value={form.credit} onChange={(e) => setForm({ ...form, credit: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Annuler</button>
            <button type="submit" className="btn-primary" data-testid="accounting-submit">Enregistrer</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!confirmId}
        onClose={() => setConfirmId(null)}
        onConfirm={() => remove(confirmId)}
        title="Supprimer cette écriture ?"
        message="Cette action est irréversible et sera tracée."
        confirmLabel="Supprimer"
      />
    </motion.div>
  );
}

function initForm(entityId) {
  return {
    entity_id: entityId === "GROUPE" ? "ROPS" : entityId,
    date: new Date().toISOString().slice(0, 10),
    journal: "VTE",
    account_code: "",
    third_party: "",
    label: "",
    debit: "",
    credit: "",
  };
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`card p-5 ${highlight ? "ring-1 ring-citron/30" : ""}`}>
      <div className="overline mb-3">{label}</div>
      <div className={`font-mono text-2xl font-semibold tabular ${highlight ? "text-citron" : "text-obsidian-100"}`}>
        {formatXAF(value)}
      </div>
    </div>
  );
}

function Th({ children, align = "left" }) { return <th className={`overline px-4 py-3 text-${align}`}>{children}</th>; }
function Td({ children, align = "left", className = "" }) { return <td className={`px-4 py-3 text-${align} ${className}`}>{children}</td>; }
